import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  MoreVertical,
  ChevronRight,
  CircleCheck,
  CircleAlert,
  CalendarDays,
  ListPlus,
  X
} from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'
import {
  getDeliveryProgress,
  getTimelineStatus,
  isCompleteStatus
} from '../lib/healthStatus'
import ConfirmModal from '../ConfirmModal'

const STATUS_BADGE = {
  draft: 'hv-badge-gray',
  active: 'hv-badge-green',
  completed: 'hv-badge-blue',
  cancelled: 'hv-badge-red',
}

const SHOW_CSV_IMPORT_TOOLS = false

const EMPTY_RIGHT_FORM = {
  project_id: '',
  category: '',
  display_name: '',
  item_prefix: '',
  custom_name: '',
  quantity: '1',
  notes: ''
}

const EMPTY_BULK_RIGHTS_FORM = {
  project_id: '',
  template_id: '',
  items: [
    {
      name: '',
      quantity: '1',
      notes: ''
    }
  ]
}

const EMPTY_CSV_IMPORT_FORM = {
  project_id: '',
  template_id: '',
  rows: [],
  fileName: '',
  errors: []
}

function normaliseBulkRightItems(items) {
  return items
    .map(item => ({
      name: item.name.trim(),
      quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
      notes: item.notes.trim()
    }))
    .filter(item => item.name)
}

function csvEscape(value) {
  const stringValue = String(value ?? '')

  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

function normaliseHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

function parseCsv(text) {
  const rows = []
  let row = []
  let value = ''
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    const nextChar = text[index + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        value += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === ',' && !inQuotes) {
      row.push(value)
      value = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') index += 1
      row.push(value)
      rows.push(row)
      row = []
      value = ''
      continue
    }

    value += char
  }

  row.push(value)
  rows.push(row)

  return rows.filter(csvRow =>
    csvRow.some(cell => String(cell || '').trim())
  )
}

function buildCustomFieldDefaults(fields, prefill = {}) {
  const customFieldDefaults = {}

  fields.forEach(field => {
    customFieldDefaults[field.field_key] =
      prefill[field.field_key] ??
      (field.field_type === 'checkbox' ? false : '')
  })

  return customFieldDefaults
}

function getDeliveryItemName(baseName, index, quantity) {
  if (quantity <= 1 || index === 0) return baseName

  return `${baseName} ${index + 1}`
}

function compareDeliveriesByCreatedOrder(a, b) {
  const createdCompare = new Date(a.created_at || 0) - new Date(b.created_at || 0)

  if (createdCompare !== 0) return createdCompare

  return String(a.id || '').localeCompare(String(b.id || ''))
}

function currencySymbol(c) {
  return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
}

function getDealDisplayCurrency(deal, allocations = []) {
  const allocationCurrencies = [
    ...new Set(
      allocations
        .map(allocation => allocation.currency)
        .filter(Boolean)
    )
  ]

  if (allocationCurrencies.length === 1) return allocationCurrencies[0]

  return deal?.currency || allocationCurrencies[0] || 'GBP'
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function formatActivityTimestamp(value) {
  if (!value) return '—'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return '—'

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatActivityDay(value) {
  if (!value) return 'Activity'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return 'Activity'

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const activityDay = new Date(date)
  activityDay.setHours(0, 0, 0, 0)

  if (activityDay.getTime() === today.getTime()) return 'Today'
  if (activityDay.getTime() === yesterday.getTime()) return 'Yesterday'

  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getRelativeTime(value) {
  if (!value) return ''

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return ''

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const absSeconds = Math.abs(diffSeconds)

  const units = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60]
  ]

  for (const [unit, seconds] of units) {
    if (absSeconds >= seconds) {
      const valueForUnit = Math.round(diffSeconds / seconds)
      return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(valueForUnit, unit)
    }
  }

  return 'just now'
}

function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function formatDateTime(value) {
  if (!value) return '—'

  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getRightName(form, templates) {
  const template = templates.find(t => t.id === form.category)
  return template?.name || ''
}

function getRightCategoryName(right) {
  return right.right_templates?.name || right.right_type || 'Other'
}

function isMeaningfulUpdate(createdAt, updatedAt) {
  if (!createdAt || !updatedAt) return false

  const created = new Date(createdAt).getTime()
  const updated = new Date(updatedAt).getTime()

  if (Number.isNaN(created) || Number.isNaN(updated)) return false

  return updated - created > 60 * 1000
}

function groupActivityByDay(items) {
  return items.reduce((groups, item) => {
    const label = formatActivityDay(item.timestamp)

    if (!groups[label]) groups[label] = []
    groups[label].push(item)

    return groups
  }, {})
}

function buildDealActivityTimeline({ deal, allocations, rights, deliveries, notes }) {
  const events = []
  const systemActor = 'Delivr system'

  if (deal?.created_at) {
    events.push({
      id: `deal-created-${deal.id}`,
      timestamp: deal.created_at,
      actor: systemActor,
      type: 'Deal',
      title: 'Deal created',
      description: `${deal.name} was created for ${deal.clients?.name || 'this sponsor'}.`
    })
  }

  if (isMeaningfulUpdate(deal?.created_at, deal?.updated_at)) {
    events.push({
      id: `deal-updated-${deal.id}`,
      timestamp: deal.updated_at,
      actor: systemActor,
      type: 'Deal',
      title: 'Deal details updated',
      description: 'Deal information was changed.'
    })
  }

  allocations.forEach(allocation => {
    if (allocation.created_at) {
      events.push({
        id: `allocation-created-${allocation.id}`,
        timestamp: allocation.created_at,
        actor: systemActor,
        type: 'Budget',
        title: 'Budget allocated',
        description: `${currencySymbol(allocation.currency || deal?.currency)}${Number(allocation.amount || 0).toLocaleString()} allocated to ${allocation.projects?.name || 'a project'}.`
      })
    }

    if (isMeaningfulUpdate(allocation.created_at, allocation.updated_at)) {
      events.push({
        id: `allocation-updated-${allocation.id}`,
        timestamp: allocation.updated_at,
        actor: systemActor,
        type: 'Budget',
        title: 'Budget allocation updated',
        description: `${allocation.projects?.name || 'Project'} allocation was changed.`
      })
    }
  })

  rights.forEach(right => {
    if (right.created_at) {
      events.push({
        id: `right-created-${right.id}`,
        timestamp: right.created_at,
        actor: systemActor,
        type: 'Right',
        title: 'Right added',
        description: `${right.name} added under ${getRightCategoryName(right)}${right.projects?.name ? ` for ${right.projects.name}` : ''}.`
      })
    }

    if (isMeaningfulUpdate(right.created_at, right.updated_at)) {
      events.push({
        id: `right-updated-${right.id}`,
        timestamp: right.updated_at,
        actor: systemActor,
        type: 'Right',
        title: 'Right updated',
        description: `${right.name} was changed.`
      })
    }
  })

  deliveries.forEach(delivery => {
    const deliveryName = delivery.name || delivery.rights?.name || 'Delivery'

    if (delivery.created_at) {
      events.push({
        id: `delivery-created-${delivery.id}`,
        timestamp: delivery.created_at,
        actor: systemActor,
        type: 'Delivery',
        title: 'Delivery created',
        description: `${deliveryName} was added${delivery.rights?.projects?.name ? ` for ${delivery.rights.projects.name}` : ''}.`
      })
    }

    if (isMeaningfulUpdate(delivery.created_at, delivery.updated_at)) {
      events.push({
        id: `delivery-updated-${delivery.id}`,
        timestamp: delivery.updated_at,
        actor: systemActor,
        type: 'Delivery',
        title: isCompleteStatus(delivery.status) ? 'Delivery marked complete' : 'Delivery updated',
        description: `${deliveryName} is now ${String(delivery.status || 'pending').replace(/_/g, ' ')}.`
      })
    }
  })

  notes.forEach(note => {
    const bodyPreview = note.body?.length > 120
      ? `${note.body.slice(0, 120)}...`
      : note.body

    events.push({
      id: `note-${note.id}`,
      timestamp: note.created_at,
      actor: note.profile?.full_name || note.profile?.email || 'Unknown user',
      type: note.parent_note_id ? 'Reply' : 'Note',
      title: note.parent_note_id ? 'Reply posted' : 'Note posted',
      description: bodyPreview || 'No note text.'
    })
  })

  return events
    .filter(event => event.timestamp)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
}

export default function DealDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { orgId, profile, can } = useUser()
  const canViewFinancials = can('financials.view')
  const canExportReports = can('reports.export')
  const canManageRights = can('rights.manage')
  const [searchParams] = useSearchParams()
  const fromProject = searchParams.get('fromProject')

  const [deal, setDeal] = useState(null)
  const [assignedProfile, setAssignedProfile] = useState(null)
  const [allocations, setAllocations] = useState([])
  const [rights, setRights] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [savingRight, setSavingRight] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'rights' ? 'rights' : 'overview')
  const [expandedProjects, setExpandedProjects] = useState({})
  const [expandedRights, setExpandedRights] = useState({})
  const [showRightForm, setShowRightForm] = useState(false)
  const [rightPrefill, setRightPrefill] = useState({})
  const [rightForm, setRightForm] = useState(EMPTY_RIGHT_FORM)
  const [showBulkRightsModal, setShowBulkRightsModal] = useState(false)
  const [bulkRightsForm, setBulkRightsForm] = useState(EMPTY_BULK_RIGHTS_FORM)
  const [savingBulkRights, setSavingBulkRights] = useState(false)
  const [showCsvImportModal, setShowCsvImportModal] = useState(false)
  const [csvImportForm, setCsvImportForm] = useState(EMPTY_CSV_IMPORT_FORM)
  const [importingCsvRights, setImportingCsvRights] = useState(false)
  const [templates, setTemplates] = useState([])
  const [templateFields, setTemplateFields] = useState({})
  const [showCopyRightsModal, setShowCopyRightsModal] = useState(false)
  const [copySourceGroup, setCopySourceGroup] = useState(null)
  const [copyTargetProjectIds, setCopyTargetProjectIds] = useState([])
  const [copySelectedCategories, setCopySelectedCategories] = useState([])
  const [copyingRights, setCopyingRights] = useState(false)
  const [notes, setNotes] = useState([])
  const [noteDraft, setNoteDraft] = useState('')
  const [replyDrafts, setReplyDrafts] = useState({})
  const [activeReplyId, setActiveReplyId] = useState(null)
  const [savingNote, setSavingNote] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)

  function goBack() {
    if (fromProject) navigate(`/projects/${fromProject}`)
    else navigate('/deals')
  }

  useEffect(() => {
    fetchDeal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function fetchDeal() {
    setLoading(true)
    setError(null)

const [
  { data: dealData, error: dealError },
  { data: allocationData, error: allocationError },
  { data: rightsData, error: rightsError },
  { data: deliveryData, error: deliveryError },
  { data: templateData, error: templateError },
  { data: notesData, error: notesError }
] = await Promise.all([
  supabase.from('secure_deals').select('*').eq('id', id).single(),
  supabase.from('secure_deal_project_allocations').select('*').eq('deal_id', id).order('created_at', { ascending: false }),
  supabase.from('rights').select('*, projects(id, name), right_templates(name)').eq('deal_id', id).order('created_at', { ascending: false }),
  supabase.from('secure_deliveries').select('*').order('created_at', { ascending: true }),
  supabase.from('right_templates').select('*').eq('org_id', orgId).eq('archived', false).order('name'),
  supabase
    .from('notes')
    .select('*')
    .eq('org_id', orgId)
    .eq('entity_type', 'deal')
    .eq('entity_id', id)
    .order('created_at', { ascending: true })
])

    if (dealError) {
      console.error('Failed to load deal:', dealError)
      setError(`Failed to load deal: ${dealError.message}`)
      setLoading(false)
      return
    }

    if (allocationError) console.error('Failed to load allocations:', allocationError)
    if (rightsError) console.error('Failed to load rights:', rightsError)
    if (deliveryError) console.error('Failed to load deliveries:', deliveryError)
    if (templateError) console.error('Failed to load templates:', templateError)
    if (notesError) console.error('Failed to load notes:', notesError)

    let profileData = null

    if (dealData?.assigned_to) {
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .eq('id', dealData.assigned_to)
        .maybeSingle()

      if (profileError) console.error('Failed to load assigned profile:', profileError)
      profileData = data || null
    }

    const dealRights = rightsData || []

    const templatesData = templateData || []

const templateIds = templatesData.map(t => t.id)

let templateFieldData = []

if (templateIds.length > 0) {
  const { data } = await supabase
    .from('right_template_fields')
    .select('*')
    .in('template_id', templateIds)
    .order('sort_order')

  templateFieldData = data || []
}

const groupedTemplateFields = {}

templateFieldData.forEach(field => {
  if (!groupedTemplateFields[field.template_id]) {
    groupedTemplateFields[field.template_id] = []
  }

  groupedTemplateFields[field.template_id].push(field)
})

    const rightIds = dealRights.map(r => r.id)

    const filteredDeliveries = (deliveryData || []).filter(d =>
      rightIds.includes(d.right_id)
    )

    const projectExpandState = {}
    ;(allocationData || []).forEach(allocation => {
      projectExpandState[allocation.id] = true
    })

    const noteRows = notesData || []
    const noteUserIds = [...new Set(noteRows.map(note => note.user_id).filter(Boolean))]
    let noteProfilesById = {}

    if (noteUserIds.length > 0) {
      const { data: noteProfiles, error: noteProfilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', noteUserIds)

      if (noteProfilesError) {
        console.error('Failed to load note profiles:', noteProfilesError)
      }

      noteProfilesById = (noteProfiles || []).reduce((acc, noteProfile) => {
        acc[noteProfile.id] = noteProfile
        return acc
      }, {})
    }

    const notesWithProfiles = noteRows.map(note => ({
      ...note,
      profile: noteProfilesById[note.user_id] || null
    }))

    setDeal(dealData)
    setAssignedProfile(profileData)
    setAllocations(allocationData || [])
    setRights(dealRights)
    setDeliveries(filteredDeliveries)
    setExpandedProjects(projectExpandState)
    setTemplates(templatesData)
    setTemplateFields(groupedTemplateFields)
    setNotes(notesWithProfiles)
    setLoading(false)
  }

function openAddRight() {
  setRightForm({
    ...EMPTY_RIGHT_FORM,
    project_id: allocations[0]?.project_id || '',
    category: ''
  })
  setRightPrefill({})
  setShowRightForm(true)
}

function openBulkRightsModal() {
  setBulkRightsForm({
    ...EMPTY_BULK_RIGHTS_FORM,
    project_id: allocations[0]?.project_id || '',
    template_id: templates[0]?.id || ''
  })
  setShowBulkRightsModal(true)
}

function closeBulkRightsModal() {
  setShowBulkRightsModal(false)
  setBulkRightsForm(EMPTY_BULK_RIGHTS_FORM)
}

function updateBulkRightItem(index, field, value) {
  setBulkRightsForm(prev => ({
    ...prev,
    items: prev.items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    )
  }))
}

function addBulkRightItem() {
  setBulkRightsForm(prev => ({
    ...prev,
    items: [
      ...prev.items,
      {
        name: '',
        quantity: '1',
        notes: ''
      }
    ]
  }))
}

function removeBulkRightItem(index) {
  setBulkRightsForm(prev => ({
    ...prev,
    items: prev.items.length > 1
      ? prev.items.filter((_, itemIndex) => itemIndex !== index)
      : prev.items
  }))
}

function openCsvImportModal() {
  setCsvImportForm({
    ...EMPTY_CSV_IMPORT_FORM,
    project_id: allocations[0]?.project_id || '',
    template_id: templates[0]?.id || ''
  })
  setShowCsvImportModal(true)
}

function closeCsvImportModal() {
  setShowCsvImportModal(false)
  setCsvImportForm(EMPTY_CSV_IMPORT_FORM)
}

function getCsvTemplateHeaders(templateId = csvImportForm.template_id) {
  const fields = templateFields[templateId] || []
  const fieldLabels = fields.map(field => field.label || field.field_key)

  return ['right_name', 'quantity', 'notes', 'delivery_name', ...fieldLabels]
}

function downloadCsvTemplate() {
  const selectedTemplate = templates.find(template => template.id === csvImportForm.template_id)

  if (!selectedTemplate) {
    toast.error('Select a right type before downloading the template')
    return
  }

  const headers = getCsvTemplateHeaders()
  const csv = `${headers.map(csvEscape).join(',')}\n`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = `delivr-${selectedTemplate.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-rights-template.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function buildCsvRows(text, templateId = csvImportForm.template_id) {
  const parsedRows = parseCsv(text)

  if (parsedRows.length < 2) {
    return {
      rows: [],
      errors: ['CSV must include a header row and at least one right row.']
    }
  }

  const headers = parsedRows[0].map(header => header.trim())
  const normalisedHeaders = headers.map(normaliseHeader)
  const errors = []
  const selectedTemplate = templates.find(template => template.id === templateId)
  const rows = parsedRows.slice(1).map((cells, rowIndex) => {
    const values = {}

    normalisedHeaders.forEach((header, headerIndex) => {
      values[header] = String(cells[headerIndex] || '').trim()
    })

    return {
      rowNumber: rowIndex + 2,
      values
    }
  }).filter(row =>
    Object.values(row.values).some(value => value)
  )

  if (!selectedTemplate) {
    errors.push('Select a right type before importing.')
  }

  const requiredHeaders = ['right_name']

  requiredHeaders.forEach(header => {
    if (!normalisedHeaders.includes(header)) {
      errors.push(`Missing required header: ${header}`)
    }
  })

  rows.forEach(row => {
    if (!row.values.right_name) {
      errors.push(`Row ${row.rowNumber}: right_name is required.`)
    }

    if (
      row.values.right_type &&
      selectedTemplate &&
      row.values.right_type.toLowerCase() !== selectedTemplate.name.toLowerCase()
    ) {
      errors.push(`Row ${row.rowNumber}: right_type "${row.values.right_type}" does not match selected right type "${selectedTemplate.name}".`)
    }
  })

  return { rows, errors }
}

function handleCsvFile(file) {
  if (!file) return

  const reader = new FileReader()

  reader.onload = event => {
    const { rows, errors } = buildCsvRows(String(event.target?.result || ''), csvImportForm.template_id)

    setCsvImportForm(prev => ({
      ...prev,
      rows,
      fileName: file.name,
      errors
    }))
  }

  reader.onerror = () => {
    setCsvImportForm(prev => ({
      ...prev,
      rows: [],
      fileName: file.name,
      errors: ['Could not read the selected CSV file.']
    }))
  }

  reader.readAsText(file)
}

  async function addRight() {
    const selectedTemplate = templates.find(t => t.id === rightForm.category)

    const rightName = getRightName(rightForm, templates)

    if (!rightForm.project_id) {
      toast.error('Please select a project allocation')
      return
    }

    if (!rightName) {
      toast.error('Right name is required')
      return
    }

    setSavingRight(true)

    const quantity = Number(rightForm.quantity) || 1
    const displayName = rightForm.display_name.trim() || rightName
    const itemPrefix = rightForm.item_prefix.trim() || displayName

    const { data: newRight, error: rightError } = await supabase
      .from('rights')
      .insert([
        withOrg({
          deal_id: id,
          project_id: rightForm.project_id,
          name: displayName,
          right_type: rightName,
template_id: selectedTemplate?.id || null,
          quantity,
          notes: rightForm.notes || null
        }, orgId)
      ])
      .select()
      .single()

    if (rightError) {
      console.error('Failed to add right:', rightError)
      toast.error(`Failed to add right: ${rightError.message}`)
      setSavingRight(false)
      return
    }

const fields = templateFields[selectedTemplate?.id] || []
const customFieldDefaults = buildCustomFieldDefaults(fields, rightPrefill)

const deliveryRows = Array.from({ length: quantity }, (_, index) =>
  withOrg({
    right_id: newRight.id,
    name: getDeliveryItemName(itemPrefix, index, quantity),
    status: 'pending',
    custom_fields: customFieldDefaults
  }, orgId)
)

    if (deliveryRows.length > 0) {
      const { error: deliveryError } = await supabase
        .from('deliveries')
        .insert(deliveryRows)

      if (deliveryError) {
        console.error('Failed to create delivery items:', deliveryError)
        toast.error(`Right added, but delivery items failed: ${deliveryError.message}`)
        setSavingRight(false)
        fetchDeal()
        return
      }
    }

    toast.success('Right added')
    setShowRightForm(false)
    setRightForm(EMPTY_RIGHT_FORM)
    setSavingRight(false)
    fetchDeal()
  }

  async function bulkCreateRights() {
    const parsedRights = normaliseBulkRightItems(bulkRightsForm.items)
    const selectedTemplate = templates.find(t => t.id === bulkRightsForm.template_id)

    if (!bulkRightsForm.project_id) {
      toast.error('Please select a project allocation')
      return
    }

    if (!selectedTemplate) {
      toast.error('Please select a right type')
      return
    }

    if (parsedRights.length === 0) {
      toast.error('Paste at least one right name')
      return
    }

    setSavingBulkRights(true)

    const rightsPayload = parsedRights.map(right =>
      withOrg({
        deal_id: id,
        project_id: bulkRightsForm.project_id,
        name: right.name,
        right_type: selectedTemplate.name,
        template_id: selectedTemplate.id,
        quantity: right.quantity,
        notes: right.notes || null
      }, orgId)
    )

    const { data: createdRights, error: rightError } = await supabase
      .from('rights')
      .insert(rightsPayload)
      .select()

    if (rightError) {
      console.error('Failed to bulk create rights:', rightError)
      toast.error(`Failed to create rights: ${rightError.message}`)
      setSavingBulkRights(false)
      return
    }

    const fields = templateFields[selectedTemplate.id] || []
    const customFieldDefaults = buildCustomFieldDefaults(fields)
    const deliveryRows = (createdRights || []).flatMap((right, rightIndex) => {
      const sourceRight = parsedRights[rightIndex]
      const quantity = Number(sourceRight?.quantity || right.quantity || 1)

      return Array.from({ length: quantity }, (_, deliveryIndex) =>
        withOrg({
          right_id: right.id,
          name: getDeliveryItemName(right.name, deliveryIndex, quantity),
          status: 'pending',
          custom_fields: customFieldDefaults
        }, orgId)
      )
    })

    if (deliveryRows.length > 0) {
      const { error: deliveryError } = await supabase
        .from('deliveries')
        .insert(deliveryRows)

      if (deliveryError) {
        console.error('Failed to bulk create delivery items:', deliveryError)
        toast.error(`Rights created, but delivery items failed: ${deliveryError.message}`)
        setSavingBulkRights(false)
        fetchDeal()
        return
      }
    }

    toast.success(`Created ${(createdRights || []).length} rights`)
    setSavingBulkRights(false)
    closeBulkRightsModal()
    fetchDeal()
  }

  async function importCsvRights() {
    const selectedTemplate = templates.find(template => template.id === csvImportForm.template_id)

    if (!csvImportForm.project_id) {
      toast.error('Please select a project allocation')
      return
    }

    if (!selectedTemplate) {
      toast.error('Please select a right type')
      return
    }

    if (csvImportForm.errors.length > 0) {
      toast.error('Fix CSV errors before importing')
      return
    }

    if (csvImportForm.rows.length === 0) {
      toast.error('Select a CSV file with rights to import')
      return
    }

    const parsedRows = csvImportForm.rows.map(row => {
      const quantity = Math.max(1, Number.parseInt(row.values.quantity, 10) || 1)

      return {
        row,
        template: selectedTemplate,
        name: row.values.right_name,
        quantity,
        notes: row.values.notes || '',
        deliveryName: row.values.delivery_name || row.values.right_name
      }
    })

    setImportingCsvRights(true)

    const rightsPayload = parsedRows.map(item =>
      withOrg({
        deal_id: id,
        project_id: csvImportForm.project_id,
        name: item.name,
        right_type: selectedTemplate.name,
        template_id: selectedTemplate.id,
        quantity: item.quantity,
        notes: item.notes || null
      }, orgId)
    )

    const { data: createdRights, error: rightError } = await supabase
      .from('rights')
      .insert(rightsPayload)
      .select()

    if (rightError) {
      console.error('Failed to import CSV rights:', rightError)
      toast.error(`Failed to import rights: ${rightError.message}`)
      setImportingCsvRights(false)
      return
    }

    const deliveryRows = (createdRights || []).flatMap((right, rightIndex) => {
      const sourceRow = parsedRows[rightIndex]
      const fields = templateFields[sourceRow.template.id] || []
      const customFields = buildCustomFieldDefaults(fields)

      fields.forEach(field => {
        const labelKey = normaliseHeader(field.label)
        const fieldKey = normaliseHeader(field.field_key)
        const value = sourceRow.row.values[labelKey] ?? sourceRow.row.values[fieldKey]

        if (value !== undefined && value !== '') {
          customFields[field.field_key] = field.field_type === 'checkbox'
            ? ['true', 'yes', '1', 'y'].includes(String(value).toLowerCase())
            : value
        }
      })

      return Array.from({ length: sourceRow.quantity }, (_, deliveryIndex) =>
        withOrg({
          right_id: right.id,
          name: getDeliveryItemName(sourceRow.deliveryName, deliveryIndex, sourceRow.quantity),
          status: 'pending',
          custom_fields: customFields
        }, orgId)
      )
    })

    if (deliveryRows.length > 0) {
      const { error: deliveryError } = await supabase
        .from('deliveries')
        .insert(deliveryRows)

      if (deliveryError) {
        console.error('Failed to create CSV delivery items:', deliveryError)
        toast.error(`Rights imported, but delivery items failed: ${deliveryError.message}`)
        setImportingCsvRights(false)
        fetchDeal()
        return
      }
    }

    toast.success(`Imported ${(createdRights || []).length} rights`)
    setImportingCsvRights(false)
    closeCsvImportModal()
    fetchDeal()
  }


  function openCopyRights(group) {
    const sourceProjectId = group?.allocation?.project_id
    const categories = [...new Set((group?.rights || []).map(getRightCategoryName))]

    setCopySourceGroup(group)
    setCopySelectedCategories(categories)
    setCopyTargetProjectIds(
      allocations
        .filter(allocation => allocation.project_id !== sourceProjectId)
        .map(allocation => allocation.project_id)
    )
    setShowCopyRightsModal(true)
  }

  function closeCopyRightsModal() {
    setShowCopyRightsModal(false)
    setCopySourceGroup(null)
    setCopyTargetProjectIds([])
    setCopySelectedCategories([])
  }

  function toggleCopyTarget(projectId) {
    setCopyTargetProjectIds(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    )
  }

  function toggleCopyCategory(categoryName) {
    setCopySelectedCategories(prev =>
      prev.includes(categoryName)
        ? prev.filter(name => name !== categoryName)
        : [...prev, categoryName]
    )
  }

  function selectAllCopyCategories() {
    setCopySelectedCategories(
      [...new Set((copySourceGroup?.rights || []).map(getRightCategoryName))]
    )
  }

  async function copyRightsToProjects() {
    if (!copySourceGroup) return

    if (copyTargetProjectIds.length === 0) {
      toast.error('Select at least one target project')
      return
    }

    const sourceRights = (copySourceGroup.rights || []).filter(right =>
      copySelectedCategories.includes(getRightCategoryName(right))
    )

    if (sourceRights.length === 0) {
      toast.error('Select at least one category to copy')
      return
    }

    setCopyingRights(true)

    for (const targetProjectId of copyTargetProjectIds) {
      for (const sourceRight of sourceRights) {
        const { data: newRight, error: rightError } = await supabase
          .from('rights')
          .insert([
            withOrg({
              deal_id: id,
              project_id: targetProjectId,
              name: sourceRight.name,
              right_type: sourceRight.right_type,
              template_id: sourceRight.template_id || null,
              quantity: sourceRight.quantity || sourceRight.total || 0,
              notes: sourceRight.notes || null
            }, orgId)
          ])
          .select()
          .single()

        if (rightError) {
          console.error('Failed to copy right:', rightError)
          toast.error(`Failed to copy right: ${rightError.message}`)
          setCopyingRights(false)
          return
        }

        const sourceDeliveries = sourceRight.deliveries || []

        if (sourceDeliveries.length > 0) {
          const deliveryRows = sourceDeliveries.map(delivery =>
            withOrg({
              right_id: newRight.id,
              name: delivery.name || 'Delivery item',
              status: 'pending',
              custom_fields: delivery.custom_fields || {}
            }, orgId)
          )

          const { error: deliveryError } = await supabase
            .from('deliveries')
            .insert(deliveryRows)

          if (deliveryError) {
            console.error('Failed to copy deliveries:', deliveryError)
            toast.error(`Right copied, but deliveries failed: ${deliveryError.message}`)
            setCopyingRights(false)
            fetchDeal()
            return
          }
        }
      }
    }

    toast.success('Rights copied')
    setCopyingRights(false)
    closeCopyRightsModal()
    fetchDeal()
  }

  async function addNote(parentNoteId = null) {
    const body = parentNoteId ? (replyDrafts[parentNoteId] || '') : noteDraft

    if (!body.trim()) {
      toast.error('Note cannot be empty')
      return
    }

    setSavingNote(true)

    const { error: noteError } = await supabase
      .from('notes')
      .insert([withOrg({
        entity_type: 'deal',
        entity_id: id,
        parent_note_id: parentNoteId,
        user_id: profile?.id || null,
        body: body.trim()
      }, orgId)])

    setSavingNote(false)

    if (noteError) {
      toast.error(`Failed to add note: ${noteError.message}`)
      return
    }

    if (parentNoteId) {
      setReplyDrafts(prev => ({ ...prev, [parentNoteId]: '' }))
      setActiveReplyId(null)
      toast.success('Reply added')
    } else {
      setNoteDraft('')
      toast.success('Note added')
    }

    fetchDeal()
  }

  function updateDeliveryLocal(deliveryId, field, value) {
    setDeliveries(prev =>
      prev.map(delivery =>
        delivery.id === deliveryId
          ? { ...delivery, [field]: value }
          : delivery
      )
    )
  }

  async function saveDeliveryField(deliveryId, field, value) {
    const { error: updateError } = await supabase
      .from('deliveries')
      .update({ [field]: value || null })
      .eq('id', deliveryId)

    if (updateError) {
      console.error('Failed to update delivery:', updateError)
      toast.error(`Failed to update delivery: ${updateError.message}`)
      fetchDeal()
    }
  }

  async function toggleDeliveryDone(delivery) {
    const nextStatus = delivery.status === 'delivered' || delivery.status === 'approved'
      ? 'pending'
      : 'delivered'

    updateDeliveryLocal(delivery.id, 'status', nextStatus)

    const { error: updateError } = await supabase
      .from('deliveries')
      .update({ status: nextStatus })
      .eq('id', delivery.id)

    if (updateError) {
      console.error('Failed to update delivery:', updateError)
      toast.error(`Failed to update delivery: ${updateError.message}`)
      fetchDeal()
    }
  }

  const allocationRows = useMemo(() => {
    return allocations.map(allocation => {
      const projectRights = rights.filter(r => r.project_id === allocation.project_id)
      const projectRightIds = projectRights.map(r => r.id)
      const projectDeliveries = deliveries.filter(d => projectRightIds.includes(d.right_id))
      const completed = projectDeliveries.filter(d => d.status === 'delivered' || d.status === 'approved').length
      const total = projectDeliveries.length
      const pct = total > 0 ? Math.round((completed / total) * 100) : 0

      return {
        ...allocation,
        rights: projectRights,
        deliveries: projectDeliveries,
        completed,
        total,
        pct
      }
    })
  }, [allocations, rights, deliveries])

  const rightsByProject = useMemo(() => {
    return allocationRows.map(allocation => {
      const rightRows = allocation.rights.map(right => {
        const rightDeliveries = deliveries
          .filter(d => d.right_id === right.id)
          .slice()
          .sort(compareDeliveriesByCreatedOrder)
        const completed = rightDeliveries.filter(d => d.status === 'delivered' || d.status === 'approved').length
        const total = rightDeliveries.length || Number(right.quantity || 0)
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0

        return {
          ...right,
          deliveries: rightDeliveries,
          completed,
          total,
          pct
        }
      })

      const projectTotal = rightRows.reduce((sum, r) => sum + r.total, 0)
      const projectComplete = rightRows.reduce((sum, r) => sum + r.completed, 0)
      const projectPct = projectTotal > 0 ? Math.round((projectComplete / projectTotal) * 100) : 0

      return {
        allocation,
        rights: rightRows,
        projectTotal,
        projectComplete,
        projectPct
      }
    })
  }, [allocationRows, deliveries])

  const bulkParsedRights = useMemo(
    () => normaliseBulkRightItems(bulkRightsForm.items),
    [bulkRightsForm.items]
  )

  const bulkDeliveryCount = useMemo(
    () => bulkParsedRights.reduce((sum, right) => sum + right.quantity, 0),
    [bulkParsedRights]
  )

  const csvDeliveryCount = useMemo(
    () => csvImportForm.rows.reduce((sum, row) => {
      const quantity = Math.max(1, Number.parseInt(row.values.quantity, 10) || 1)
      return sum + quantity
    }, 0),
    [csvImportForm.rows]
  )

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading...
      </div>
    )
  }

  if (error || !deal) {
    return (
      <div className="hv-page">
        <button className="hv-btn hv-btn-ghost" onClick={goBack} style={{ marginBottom: '20px' }}>
          ← {fromProject ? 'Back to Project' : 'Back to Deals'}
        </button>

        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px'
        }}>
          {error || 'Deal not found.'}
        </div>
      </div>
    )
  }

  const displayCurrency = getDealDisplayCurrency(deal, allocations)
  const sym = currencySymbol(displayCurrency)
  const dealValue = Number(deal.value) || 0
  const allocatedTotal = allocations.reduce((sum, a) => sum + (Number(a.amount) || 0), 0)
  const completeDeliveries = deliveries.filter(d =>
    isCompleteStatus(d.status)
  ).length

  const completionRate = getDeliveryProgress(deliveries, deal)
  const progressScore = completionRate
  const progressColor = '#6366F1'
  const timelineStatus = getTimelineStatus(deal, deliveries)

  function exportDealReport() {
    const escapeHtml = value => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

    const completedCount = completeDeliveries
    const totalCount = deliveries.length

    const projectSections = rightsByProject.map(group => {
      const projectName = group.allocation?.projects?.name || 'Project'
      const projectPct = group.projectPct || 0
      const projectComplete = group.projectComplete || 0
      const projectTotal = group.projectTotal || 0

      const templateGroups = {}

      ;(group.rights || []).forEach(right => {
        const templateName = right.right_templates?.name || right.right_type || 'Other'

        if (!templateGroups[templateName]) {
          templateGroups[templateName] = []
        }

        templateGroups[templateName].push(right)
      })

      const rightRows = Object.entries(templateGroups).map(([templateName, templateRights]) => {
        const rows = templateRights.map(right => {
          const rightDeliveries = right.deliveries || []
          const deliveryRows = rightDeliveries.map(delivery => {
            const done = delivery.status === 'delivered' || delivery.status === 'approved'
            const customFields = delivery.custom_fields || {}
            const customFieldText = Object.entries(customFields)
              .filter(([, value]) => value !== null && value !== undefined && value !== '')
              .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
              .join(' · ')

            return `
              <tr>
                <td>${escapeHtml(delivery.name || 'Delivery item')}</td>
                <td><span class="status ${done ? 'done' : 'pending'}">${done ? 'Complete' : 'Pending'}</span></td>
                <td>${escapeHtml(formatDate(delivery.delivery_date))}</td>
                <td>${escapeHtml(customFieldText || '—')}</td>
                <td>${escapeHtml(delivery.url || delivery.file_url || '—')}</td>
              </tr>
            `
          }).join('')

          return `
            <div class="right-block">
              <div class="right-heading">
                <div>
                  <h4>${escapeHtml(right.name || right.right_type || 'Right')}</h4>
                  <p>${escapeHtml(right.completed || 0)} / ${escapeHtml(right.total || 0)} delivered</p>
                </div>
                <div class="right-score">${escapeHtml(right.pct || 0)}%</div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Delivery</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Fields</th>
                    <th>Proof / URL</th>
                  </tr>
                </thead>
                <tbody>
                  ${deliveryRows || '<tr><td colspan="5">No delivery items yet.</td></tr>'}
                </tbody>
              </table>
            </div>
          `
        }).join('')

        return `
          <section class="template-section">
            <h3>${escapeHtml(templateName)}</h3>
            ${rows}
          </section>
        `
      }).join('')

      return `
        <section class="project-section">
          <div class="project-heading">
            <div>
              <h2>${escapeHtml(projectName)}</h2>
              <p>${escapeHtml(projectComplete)} / ${escapeHtml(projectTotal)} delivery items complete</p>
            </div>
            <div class="project-score">${escapeHtml(projectPct)}%</div>
          </div>
          ${rightRows || '<p class="muted">No rights have been added for this project.</p>'}
        </section>
      `
    }).join('')

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(deal.name)} Report</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              background: #F8FAFC;
              color: #0F172A;
              font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              line-height: 1.45;
            }
            .page {
              max-width: 1120px;
              margin: 0 auto;
              padding: 34px;
            }
            .topbar {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 24px;
              margin-bottom: 26px;
            }
            .eyebrow {
              color: #6366F1;
              font-size: 12px;
              font-weight: 800;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              margin-bottom: 8px;
            }
            h1, h2, h3, h4, p { margin: 0; }
            h1 { font-size: 32px; letter-spacing: -0.04em; }
            h2 { font-size: 20px; letter-spacing: -0.02em; }
            h3 { font-size: 15px; margin: 20px 0 12px; }
            h4 { font-size: 14px; }
            .muted { color: #64748B; font-size: 13px; }
            .print-btn {
              border: 0;
              border-radius: 10px;
              background: #4F46E5;
              color: white;
              font-weight: 800;
              padding: 10px 14px;
              cursor: pointer;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 12px;
              margin-bottom: 22px;
            }
            .card, .project-section {
              background: white;
              border: 1px solid #E2E8F0;
              border-radius: 16px;
              box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
            }
            .card { padding: 16px; }
            .label {
              color: #64748B;
              font-size: 11px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.06em;
              margin-bottom: 8px;
            }
            .value { font-size: 20px; font-weight: 900; }
            .project-section { padding: 22px; margin-bottom: 18px; }
            .project-heading, .right-heading {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 18px;
            }
            .project-heading {
              border-bottom: 1px solid #E2E8F0;
              padding-bottom: 14px;
              margin-bottom: 16px;
            }
            .project-score, .right-score {
              width: 58px;
              height: 58px;
              border-radius: 50%;
              background: #EEF2FF;
              color: #4F46E5;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 900;
            }
            .right-score { width: 44px; height: 44px; font-size: 13px; }
            .template-section {
              border-top: 1px solid #F1F5F9;
              padding-top: 8px;
            }
            .right-block {
              border: 1px solid #E2E8F0;
              border-radius: 12px;
              padding: 14px;
              margin-bottom: 12px;
            }
            .right-heading { margin-bottom: 12px; }
            .right-heading p { color: #64748B; font-size: 12px; margin-top: 3px; }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            th {
              text-align: left;
              color: #64748B;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.06em;
              background: #F8FAFC;
            }
            th, td {
              border-bottom: 1px solid #E2E8F0;
              padding: 9px 8px;
              vertical-align: top;
            }
            tr:last-child td { border-bottom: 0; }
            .status {
              display: inline-flex;
              border-radius: 999px;
              padding: 3px 8px;
              font-size: 10px;
              font-weight: 800;
            }
            .status.done { background: #DCFCE7; color: #166534; }
            .status.pending { background: #F1F5F9; color: #475569; }
            .footer {
              color: #64748B;
              font-size: 11px;
              margin-top: 26px;
              text-align: center;
            }
            @media print {
              body { background: white; }
              .page { padding: 0; max-width: none; }
              .print-btn { display: none; }
              .project-section, .card { break-inside: avoid; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <main class="page">
            <div class="topbar">
              <div>
                <div class="eyebrow">Sponsorship Delivery Report</div>
                <h1>${escapeHtml(deal.name)}</h1>
                <p class="muted">${escapeHtml(deal.clients?.name || 'Client')} · Generated ${escapeHtml(formatDate(new Date()))}</p>
              </div>
              <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
            </div>

            <div class="summary-grid">
              <div class="card"><div class="label">Deal Value</div><div class="value">${escapeHtml(sym)}${escapeHtml(dealValue.toLocaleString())}</div></div>
              <div class="card"><div class="label">Allocated</div><div class="value">${escapeHtml(sym)}${escapeHtml(allocatedTotal.toLocaleString())}</div></div>
              <div class="card"><div class="label">Progress</div><div class="value">${escapeHtml(completionRate)}%</div></div>
              <div class="card"><div class="label">Completed</div><div class="value">${escapeHtml(completedCount)} / ${escapeHtml(totalCount)}</div></div>
              <div class="card"><div class="label">Status</div><div class="value">${escapeHtml(deal.status || '—')}</div></div>
            </div>

            ${projectSections || '<div class="card"><p class="muted">No rights or deliveries have been added to this deal yet.</p></div>'}

            <div class="footer">Generated by Delivr</div>
          </main>
        </body>
      </html>
    `

    const reportWindow = window.open('', '_blank')

    if (!reportWindow) {
      toast.error('Your browser blocked the report window. Please allow popups and try again.')
      return
    }

    reportWindow.document.open()
    reportWindow.document.write(html)
    reportWindow.document.close()
    reportWindow.focus()
  }

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'rights', label: 'Rights & Deliveries' },
  { id: 'budget', label: 'Budget' },
  { id: 'notes', label: 'Notes' },
  { id: 'documents', label: 'Documents' },
  { id: 'activity', label: 'Activity' }
]

  const selectedBulkTemplate = templates.find(t => t.id === bulkRightsForm.template_id)
  const selectedBulkFields = templateFields[bulkRightsForm.template_id] || []

  const activityTimeline = buildDealActivityTimeline({
    deal,
    allocations,
    rights,
    deliveries,
    notes
  })

  return (
    <div className="hv-page">
      <style>{`
        .deal-detail-top {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 26px;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .deal-breadcrumb {
          font-size: 12px;
          color: var(--hv-text-muted);
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .deal-title-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .deal-detail-title {
          font-size: 24px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.7px;
        }

        .deal-meta-row {
          display: flex;
          align-items: center;
          gap: 20px;
          font-size: 12px;
          color: var(--hv-text-muted);
        }

        .deal-logo-dot {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #EEF2FF;
          color: #4F46E5;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 800;
        }

        .deal-metrics {
          display: flex;
          align-items: center;
          gap: 36px;
        }

        .deal-metric-value {
          font-size: 22px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.5px;
          margin-bottom: 4px;
        }

        .deal-metric-label {
          font-size: 11px;
          color: var(--hv-text-muted);
        }

        .deal-progress-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .deal-progress-ring {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: conic-gradient(${progressColor} ${progressScore * 3.6}deg, #EEF2F7 0deg);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .deal-progress-ring-inner {
          width: 31px;
          height: 31px;
          border-radius: 50%;
          background: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 800;
          color: ${progressColor};
        }

        .deal-progress-label {
          font-size: 11px;
          font-weight: 800;
          color: ${progressColor};
          margin-top: 4px;
          text-align: center;
        }

        .deal-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 9px;
          border-radius: 999px;
          border: 1px solid ${timelineStatus.border};
          background: ${timelineStatus.background};
          color: ${timelineStatus.color};
          font-size: 11px;
          font-weight: 800;
          white-space: nowrap;
        }

        .deal-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: ${timelineStatus.color};
        }

        .deal-tabs {
          display: flex;
          align-items: center;
          gap: 28px;
          border-bottom: 1px solid var(--hv-border);
          margin-bottom: 18px;
        }

        .deal-tab {
          border: none;
          background: transparent;
          padding: 14px 0;
          font-size: 12px;
          font-weight: 700;
          color: var(--hv-text-muted);
          cursor: pointer;
          border-bottom: 2px solid transparent;
        }

        .deal-tab--active {
          color: #4F46E5;
          border-bottom-color: #4F46E5;
        }

        .deal-main-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 360px;
          gap: 28px;
        }

        .deal-overview-grid {
          display: grid;
          grid-template-columns: 0.85fr 1.15fr 1.25fr;
          gap: 14px;
          align-items: stretch;
        }

        .concept-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15,21,35,0.05);
        }

        .concept-card-pad {
          padding: 20px;
        }

        .section-title {
          font-size: 14px;
          font-weight: 800;
          color: var(--hv-text-primary);
          margin-bottom: 18px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: 92px 1fr;
          row-gap: 14px;
          column-gap: 12px;
          font-size: 12.5px;
        }

        .detail-label {
          color: var(--hv-text-muted);
        }

        .detail-value {
          color: var(--hv-text-primary);
          font-weight: 700;
        }

        .budget-bar {
          display: flex;
          width: 100%;
          height: 10px;
          border-radius: 999px;
          overflow: hidden;
          background: #EEF2F7;
          margin: 18px 0 20px;
        }

        .budget-segment {
          height: 100%;
        }

        .budget-row {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 14px;
          align-items: center;
          padding: 14px 0;
          border-bottom: 1px solid var(--hv-border-light);
          font-size: 12.5px;
        }

        .budget-row:last-child {
          border-bottom: none;
        }

        .budget-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 8px;
        }

        .rights-project-row {
          padding: 14px 0;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .rights-project-row:last-child {
          border-bottom: none;
        }

        .rights-project-header {
          display: grid;
          grid-template-columns: 1fr 220px 190px;
          gap: 14px;
          align-items: center;
          font-size: 12.5px;
          font-weight: 800;
          color: var(--hv-text-primary);
          cursor: pointer;
        }

        .mini-progress {
          height: 5px;
          background: #EEF2F7;
          border-radius: 999px;
          overflow: hidden;
        }

        .mini-progress-fill {
          height: 100%;
          border-radius: 999px;
        }

        .right-line {
          display: grid;
          grid-template-columns: 16px 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 10px 12px;
          border: 1px solid var(--hv-border-light);
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 12px;
          cursor: pointer;
          background: #fff;
        }

        .right-line:hover {
          background: #FAFBFF;
        }

        .delivery-grid {
          border: 1px solid var(--hv-border-light);
          border-radius: 10px;
          overflow: hidden;
          margin: 8px 0 14px 26px;
          background: #fff;
        }

        .delivery-grid-head,
        .delivery-grid-row {
          display: grid;
          grid-template-columns: 1.5fr 130px 140px 1.4fr 1.3fr 70px;
          gap: 12px;
          align-items: center;
        }

        .delivery-grid-head {
          background: #FAFBFF;
          padding: 10px 12px;
          border-bottom: 1px solid var(--hv-border-light);
          font-size: 10.5px;
          font-weight: 800;
          color: var(--hv-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .delivery-grid-row {
          padding: 9px 12px;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .delivery-grid-row:last-child {
          border-bottom: none;
        }

        .delivery-grid-row input,
        .delivery-grid-row select {
          height: 34px;
          font-size: 12px;
        }

        .done-cell {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .done-cell input {
          width: 15px;
          height: 15px;
          cursor: pointer;
        }

        .activity-panel {
          border-left: 1px solid var(--hv-border);
          padding: 4px 0 0 30px;
          min-height: 520px;
        }

        .activity-panel-title {
          font-size: 14px;
          font-weight: 800;
          color: var(--hv-text-primary);
        }

        .activity-group-label {
          font-size: 11px;
          font-weight: 800;
          color: var(--hv-text-muted);
          margin: 0 0 8px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .activity-item {
          display: grid;
          grid-template-columns: 36px minmax(0, 1fr) auto;
          gap: 14px;
          padding: 16px 0;
          position: relative;
          border-bottom: 1px solid var(--hv-border-light);
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-dot {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #EEF2FF;
          color: #4F46E5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 800;
          font-family: var(--hv-mono);
          z-index: 1;
        }

        .activity-item:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 16px;
          top: 50px;
          bottom: -1px;
          width: 1px;
          background: var(--hv-border);
        }

        .right-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 24px;
        }

        .right-modal {
          width: 560px;
          max-width: 100%;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
          background: #fff;
          border-radius: 16px;
          border: 1px solid var(--hv-border);
          box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
          padding: 24px;
        }

        .placeholder-panel {
          padding: 42px;
          text-align: center;
          color: var(--hv-text-muted);
          font-size: 13px;
        }

        .notes-board {
          max-width: 980px;
        }

        .note-composer {
          border: 1px solid var(--hv-border);
          border-radius: 12px;
          background: #FAFBFF;
          padding: 14px;
          margin-bottom: 18px;
        }

        .note-card {
          border: 1px solid var(--hv-border-light);
          border-radius: 12px;
          background: #fff;
          padding: 14px;
        }

        .note-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: #EEF2FF;
          color: #4F46E5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
          flex-shrink: 0;
        }

        .note-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--hv-text-muted);
          margin-bottom: 8px;
        }

        .note-author {
          font-weight: 800;
          color: var(--hv-text-primary);
        }

        .note-body {
          white-space: pre-wrap;
          line-height: 1.6;
          font-size: 13px;
          color: var(--hv-text-primary);
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 5px 9px;
          font-size: 11px;
          font-weight: 800;
        }
      `}</style>

      <div className="deal-breadcrumb">
        <button className="hv-btn hv-btn-ghost" onClick={goBack} style={{ padding: 0 }}>
          Deals
        </button>
        <ChevronRight size={13} />
        <span>{deal.clients?.name || 'Client'}</span>
        <ChevronRight size={13} />
        <span>{deal.name}</span>
      </div>

      <div className="deal-detail-top">
        <div>
          <div className="deal-title-row">
            <h1 className="deal-detail-title">{deal.name}</h1>
            <span className={`hv-badge ${STATUS_BADGE[deal.status] || 'hv-badge-gray'}`}>
              {deal.status?.charAt(0).toUpperCase() + deal.status?.slice(1)}
            </span>
          </div>

          <div className="deal-meta-row">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span className="deal-logo-dot">{getInitials(deal.clients?.name)}</span>
              {deal.clients?.name || 'No client'}
            </span>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <CalendarDays size={13} />
              {formatDate(deal.end_date)}
            </span>
          </div>
        </div>

        <div className="deal-metrics">
          {canViewFinancials && <div>
            <div className="deal-metric-value">{dealValue ? `${sym}${dealValue.toLocaleString()}` : '—'}</div>
            <div className="deal-metric-label">Total Value</div>
          </div>}

          {canViewFinancials && <div>
            <div className="deal-metric-value">{sym}{allocatedTotal.toLocaleString()}</div>
            <div className="deal-metric-label">Allocated</div>
          </div>}

          <div className="deal-progress-wrap">
            <div>
              <span className="deal-progress-ring">
                <span className="deal-progress-ring-inner">{progressScore}%</span>
              </span>
              <div className="deal-progress-label">Progress</div>
            </div>

            <span className="deal-status-badge">
              <span className="deal-status-dot" />
              {timelineStatus.label}
            </span>
          </div>

          {canExportReports && <button
            className="hv-btn hv-btn-primary"
            style={{ padding: '8px 12px', fontSize: 12 }}
            onClick={exportDealReport}
          >
            Export Report
          </button>}

<div style={{ position: 'relative' }}>
  <button
    className="hv-btn hv-btn-secondary"
    style={{ padding: '8px 10px' }}
    onClick={() => setActionsOpen(prev => !prev)}
  >
    <MoreVertical size={16} />
  </button>

  {actionsOpen && (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 190,
        background: '#fff',
        border: '1px solid var(--hv-border)',
        borderRadius: 12,
        boxShadow: '0 18px 45px rgba(15, 23, 42, 0.16)',
        padding: 8,
        zIndex: 100
      }}
    >
      <button
        className="hv-btn hv-btn-ghost"
        style={{ width: '100%', justifyContent: 'flex-start' }}
        onClick={() => {
          setActionsOpen(false)
          navigate('/deals', { state: { editDealId: id } })
        }}
      >
        Edit Deal
      </button>

      <button
        className="hv-btn hv-btn-ghost"
        style={{ width: '100%', justifyContent: 'flex-start' }}
        onClick={() => {
          setActionsOpen(false)
          exportDealReport()
        }}
      >
        Export Report
      </button>

    </div>
  )}
</div>
        </div>
      </div>

      <div className="deal-tabs">
        {tabs.filter(tab => canViewFinancials || tab.id !== 'budget').map(tab => (
          <button
            key={tab.id}
            className={`deal-tab ${activeTab === tab.id ? 'deal-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="deal-overview-grid">
          <DealOverviewCard deal={deal} assignedProfile={assignedProfile} formatDate={formatDate} />

          {canViewFinancials && <BudgetCard
            sym={sym}
            dealValue={dealValue}
            allocatedTotal={allocatedTotal}
            allocationRows={allocationRows}
          />}

          <RightsSummaryCard
            rightsByProject={rightsByProject}
            templateFields={templateFields}
            expandedProjects={expandedProjects}
            setExpandedProjects={setExpandedProjects}
            expandedRights={expandedRights}
            setExpandedRights={setExpandedRights}
            onAddRight={openAddRight}
            onBulkCreateRights={openBulkRightsModal}
            onImportCsvRights={SHOW_CSV_IMPORT_TOOLS ? openCsvImportModal : null}
            updateDeliveryLocal={updateDeliveryLocal}
            saveDeliveryField={saveDeliveryField}
            toggleDeliveryDone={toggleDeliveryDone}
            fetchDeal={fetchDeal}
            allocations={allocations}
            onCopyRights={openCopyRights}
            canManageRights={canManageRights}
          />
        </div>
      )}

      {activeTab === 'rights' && (
        <div className="concept-card concept-card-pad">
          <RightsSummaryCard
            rightsByProject={rightsByProject}
            templateFields={templateFields}
            expanded
            expandedProjects={expandedProjects}
            setExpandedProjects={setExpandedProjects}
            expandedRights={expandedRights}
            setExpandedRights={setExpandedRights}
            onAddRight={openAddRight}
            onBulkCreateRights={openBulkRightsModal}
            onImportCsvRights={SHOW_CSV_IMPORT_TOOLS ? openCsvImportModal : null}
            updateDeliveryLocal={updateDeliveryLocal}
            saveDeliveryField={saveDeliveryField}
            toggleDeliveryDone={toggleDeliveryDone}
            fetchDeal={fetchDeal}
            allocations={allocations}
            onCopyRights={openCopyRights}
            canManageRights={canManageRights}
          />
        </div>
      )}

      {activeTab === 'budget' && canViewFinancials && (
        <div className="concept-card concept-card-pad">
          <BudgetCard
            sym={sym}
            dealValue={dealValue}
            allocatedTotal={allocatedTotal}
            allocationRows={allocationRows}
            standalone
          />
        </div>
      )}

      {activeTab === 'notes' && (
        <NotesBoard
          notes={notes}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
          replyDrafts={replyDrafts}
          setReplyDrafts={setReplyDrafts}
          activeReplyId={activeReplyId}
          setActiveReplyId={setActiveReplyId}
          savingNote={savingNote}
          onAddNote={addNote}
        />
      )}

      {activeTab === 'documents' && (
        <div className="concept-card placeholder-panel">
          Documents area coming soon — contracts, screenshots, signed agreements and supporting files will appear here.
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="concept-card concept-card-pad">
          <ActivityPanel activity={activityTimeline} embedded />
        </div>
      )}


      {showCopyRightsModal && copySourceGroup && (
        <div className="right-modal-overlay">
          <div className="right-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                  Copy Rights
                </div>
                <div style={{ fontSize: 13, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  Copy rights from {copySourceGroup.allocation.projects?.name || 'this project'} to other projects in this deal.
                </div>
              </div>

              <button className="hv-btn hv-btn-ghost" onClick={closeCopyRightsModal}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--hv-border)',
                  borderRadius: 'var(--hv-radius)',
                  background: '#F8FAFC'
                }}
              >
                <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginBottom: 4 }}>
                  Copying from
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                  {copySourceGroup.allocation.projects?.name || 'Project'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  {(copySourceGroup.rights || []).length} rights · {copySourceGroup.projectTotal || 0} delivery items
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <label className="hv-form-label" style={{ marginBottom: 0 }}>Categories to copy</label>

                  <button
                    className="hv-btn hv-btn-ghost"
                    style={{ fontSize: 11, padding: 0, color: '#4F46E5' }}
                    onClick={selectAllCopyCategories}
                    type="button"
                  >
                    Copy all
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  {[...new Set((copySourceGroup.rights || []).map(getRightCategoryName))].map(categoryName => {
                    const categoryRights = (copySourceGroup.rights || []).filter(right =>
                      getRightCategoryName(right) === categoryName
                    )
                    const categoryDeliveries = categoryRights.reduce(
                      (sum, right) => sum + (right.deliveries?.length || 0),
                      0
                    )
                    const checked = copySelectedCategories.includes(categoryName)

                    return (
                      <label
                        key={categoryName}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '10px 12px',
                          border: '1px solid var(--hv-border)',
                          borderRadius: 'var(--hv-radius-sm)',
                          background: checked ? '#F5F3FF' : '#fff',
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCopyCategory(categoryName)}
                          />
                          {categoryName}
                        </span>

                        <span style={{ color: 'var(--hv-text-muted)', fontSize: 12 }}>
                          {categoryRights.length} rights · {categoryDeliveries} items
                        </span>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div>
                <label className="hv-form-label">Copy to</label>

                <div style={{ display: 'grid', gap: 8 }}>
                  {allocations
                    .filter(allocation => allocation.project_id !== copySourceGroup.allocation.project_id)
                    .map(allocation => {
                      const checked = copyTargetProjectIds.includes(allocation.project_id)

                      return (
                        <label
                          key={allocation.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            border: '1px solid var(--hv-border)',
                            borderRadius: 'var(--hv-radius-sm)',
                            background: checked ? '#F5F3FF' : '#fff',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 700
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCopyTarget(allocation.project_id)}
                          />
                          {allocation.projects?.name || 'Project'}
                        </label>
                      )
                    })}
                </div>
              </div>

              <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', lineHeight: 1.6 }}>
                This copies selected right categories, delivery item names and custom field values. Delivery statuses are reset to pending.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button className="hv-btn hv-btn-secondary" onClick={closeCopyRightsModal}>
                Cancel
              </button>

              <button
                className="hv-btn hv-btn-primary"
                onClick={copyRightsToProjects}
                disabled={copyingRights || copyTargetProjectIds.length === 0 || copySelectedCategories.length === 0}
              >
                {copyingRights ? 'Copying...' : 'Copy Rights'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCsvImportModal && (
        <div className="right-modal-overlay">
          <div className="right-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                  Import Rights CSV
                </div>
                <div style={{ fontSize: 13, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  Import rights using right type names and template field columns.
                </div>
              </div>

              <button
                className="hv-btn hv-btn-ghost"
                onClick={closeCsvImportModal}
                disabled={importingCsvRights}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="hv-form-label">Project Allocation</label>
                <select
                  className="hv-select"
                  value={csvImportForm.project_id}
                  onChange={e =>
                    setCsvImportForm({
                      ...csvImportForm,
                      project_id: e.target.value
                    })
                  }
                >
                  <option value="">Select project</option>

                  {allocations.map(allocation => (
                    <option key={allocation.id} value={allocation.project_id}>
                      {allocation.projects?.name || 'Project'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="hv-form-label">Right Type</label>
                <select
                  className="hv-select"
                  value={csvImportForm.template_id}
                  onChange={e =>
                    setCsvImportForm({
                      ...csvImportForm,
                      template_id: e.target.value,
                      rows: [],
                      fileName: '',
                      errors: []
                    })
                  }
                >
                  <option value="">Select right type</option>

                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="hv-form-label">CSV File</label>
                <input
                  key={csvImportForm.template_id}
                  className="hv-input"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={e => handleCsvFile(e.target.files?.[0])}
                />
              </div>

              <div
                style={{
                  border: '1px solid var(--hv-border-light)',
                  borderRadius: 12,
                  background: '#FAFBFF',
                  padding: 14
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                      Preview
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginTop: 3 }}>
                      Required header is right_name. Quantity defaults to 1.
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--hv-text-secondary)', whiteSpace: 'nowrap' }}>
                    <strong style={{ color: 'var(--hv-text-primary)' }}>{csvImportForm.rows.length}</strong> rights
                    <br />
                    <strong style={{ color: 'var(--hv-text-primary)' }}>{csvDeliveryCount}</strong> deliveries
                  </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--hv-text-secondary)', lineHeight: 1.6 }}>
                  File: <strong>{csvImportForm.fileName || 'No file selected'}</strong>
                  <br />
                  Template field headers: {getCsvTemplateHeaders().slice(4).join(', ') || 'No custom fields configured'}
                </div>
              </div>

              {csvImportForm.errors.length > 0 && (
                <div
                  style={{
                    border: '1px solid #FCA5A5',
                    borderRadius: 12,
                    background: '#FEF2F2',
                    color: '#B91C1C',
                    padding: 14,
                    fontSize: 12,
                    lineHeight: 1.6
                  }}
                >
                  {csvImportForm.errors.slice(0, 8).map(error => (
                    <div key={error}>{error}</div>
                  ))}
                  {csvImportForm.errors.length > 8 && (
                    <div>And {csvImportForm.errors.length - 8} more errors.</div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 22 }}>
              <button
                className="hv-btn hv-btn-secondary"
                onClick={downloadCsvTemplate}
                type="button"
              >
                Download Template
              </button>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  className="hv-btn hv-btn-secondary"
                  onClick={closeCsvImportModal}
                  disabled={importingCsvRights}
                >
                  Cancel
                </button>

                <button
                  className="hv-btn hv-btn-primary"
                  onClick={importCsvRights}
                  disabled={importingCsvRights || csvImportForm.rows.length === 0 || csvImportForm.errors.length > 0}
                >
                  {importingCsvRights
                    ? 'Importing...'
                    : csvImportForm.rows.length > 0
                      ? `Import ${csvImportForm.rows.length} Rights`
                      : 'Import Rights'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkRightsModal && (
        <div className="right-modal-overlay">
          <div className="right-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                  Bulk Create Rights
                </div>
                <div style={{ fontSize: 13, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  Create multiple rights from one right type.
                </div>
              </div>

              <button
                className="hv-btn hv-btn-ghost"
                onClick={closeBulkRightsModal}
                disabled={savingBulkRights}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="hv-form-label">Project Allocation</label>
                <select
                  className="hv-select"
                  value={bulkRightsForm.project_id}
                  onChange={e =>
                    setBulkRightsForm({
                      ...bulkRightsForm,
                      project_id: e.target.value
                    })
                  }
                >
                  <option value="">Select project</option>

                  {allocations.map(allocation => (
                    <option key={allocation.id} value={allocation.project_id}>
                      {allocation.projects?.name || 'Project'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="hv-form-label">Right Type</label>
                <select
                  className="hv-select"
                  value={bulkRightsForm.template_id}
                  onChange={e =>
                    setBulkRightsForm({
                      ...bulkRightsForm,
                      template_id: e.target.value
                    })
                  }
                >
                  <option value="">Select right type</option>

                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="hv-form-label">Rights</label>
                <div style={{ display: 'grid', gap: 10 }}>
                  {bulkRightsForm.items.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(180px, 1.2fr) 96px minmax(160px, 1fr) 36px',
                        gap: 10,
                        alignItems: 'end'
                      }}
                    >
                      <div>
                        <label className="hv-form-label">Right Name</label>
                        <input
                          className="hv-input"
                          placeholder="Social Post"
                          value={item.name}
                          onChange={e => updateBulkRightItem(index, 'name', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Quantity</label>
                        <input
                          className="hv-input"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={e => updateBulkRightItem(index, 'quantity', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="hv-form-label">Notes</label>
                        <input
                          className="hv-input"
                          placeholder="Optional notes..."
                          value={item.notes}
                          onChange={e => updateBulkRightItem(index, 'notes', e.target.value)}
                        />
                      </div>

                      <button
                        className="hv-btn hv-btn-ghost"
                        style={{ width: 36, height: 36, padding: 0 }}
                        onClick={() => removeBulkRightItem(index)}
                        disabled={bulkRightsForm.items.length === 1}
                        title="Remove right"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ))}

                  <button
                    className="hv-btn hv-btn-secondary"
                    style={{ justifySelf: 'start', fontSize: 12 }}
                    onClick={addBulkRightItem}
                    type="button"
                  >
                    Add Another Right
                  </button>
                </div>
              </div>

              <div
                style={{
                  border: '1px solid var(--hv-border-light)',
                  borderRadius: 12,
                  background: '#FAFBFF',
                  padding: 14
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                      Preview
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--hv-text-muted)', marginTop: 3 }}>
                      Blank right names are ignored. Each row creates one right with its own quantity and notes.
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--hv-text-secondary)', whiteSpace: 'nowrap' }}>
                    <strong style={{ color: 'var(--hv-text-primary)' }}>{bulkParsedRights.length}</strong> rights
                    <br />
                    <strong style={{ color: 'var(--hv-text-primary)' }}>{bulkDeliveryCount}</strong> deliveries
                  </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--hv-text-secondary)', lineHeight: 1.6 }}>
                  Right type: <strong>{selectedBulkTemplate?.name || 'Not selected'}</strong>
                  <br />
                  Delivery fields: {selectedBulkFields.length > 0
                    ? selectedBulkFields.map(field => field.label).join(', ')
                    : 'No custom fields configured'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button
                className="hv-btn hv-btn-secondary"
                onClick={closeBulkRightsModal}
                disabled={savingBulkRights}
              >
                Cancel
              </button>

              <button
                className="hv-btn hv-btn-primary"
                onClick={bulkCreateRights}
                disabled={savingBulkRights || bulkParsedRights.length === 0}
              >
                {savingBulkRights
                  ? 'Creating...'
                  : bulkParsedRights.length > 0
                    ? `Create ${bulkParsedRights.length} Rights`
                    : 'Create Rights'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRightForm && (
        <div className="right-modal-overlay">
          <div className="right-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                  Add Right
                </div>
                <div style={{ fontSize: 13, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                  Add a commercial right to this deal and project allocation.
                </div>
              </div>

              <button className="hv-btn hv-btn-ghost" onClick={() => setShowRightForm(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="hv-form-label">Project Allocation</label>
                <select
  className="hv-select"
  value={rightForm.project_id}
onChange={e =>
  setRightForm({
    ...rightForm,
    project_id: e.target.value
  })
}
>
  <option value="">Select project</option>

  {allocations.map(allocation => (
    <option key={allocation.id} value={allocation.project_id}>
      {allocation.projects?.name || 'Project'}
    </option>
  ))}
</select>
              </div>

<div>
  <label className="hv-form-label">Right Type</label>

  <select
    className="hv-select"
    value={rightForm.category}
onChange={e => {
  setRightForm({
    ...rightForm,
    category: e.target.value
  })
  setRightPrefill({})
}}
  >
    <option value="">Select right type</option>

    {templates.map(template => (
      <option key={template.id} value={template.id}>
        {template.name}
      </option>
    ))}
  </select>
</div>

<div>
  <label className="hv-form-label">Right Name</label>
  <input
    className="hv-input"
    placeholder="e.g. 10 x Social Posts"
    value={rightForm.display_name}
    onChange={e =>
      setRightForm({
        ...rightForm,
        display_name: e.target.value
      })
    }
  />
</div>

<div>
  <label className="hv-form-label">Item Name Prefix</label>
  <input
    className="hv-input"
    placeholder="e.g. Social Post"
    value={rightForm.item_prefix}
    onChange={e =>
      setRightForm({
        ...rightForm,
        item_prefix: e.target.value
      })
    }
  />
</div>

              <div>
                <label className="hv-form-label">Quantity</label>
                <input
                  className="hv-input"
                  type="number"
                  min="1"
                  value={rightForm.quantity}
                  onChange={e => setRightForm({ ...rightForm, quantity: e.target.value })}
                />
              </div>
 
               {rightForm.category && (templateFields[rightForm.category] || []).length > 0 && (
  <div style={{ borderTop: '1px solid var(--hv-border)', paddingTop: 14 }}>
    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>
      Pre-fill delivery fields
    </div>

    <div style={{ display: 'grid', gap: 12 }}>
      {(templateFields[rightForm.category] || []).map(field => {
        const value = rightPrefill[field.field_key] ?? ''

        if (field.field_type === 'select') {
          const options = Array.isArray(field.options)
            ? field.options
            : String(field.options || '')
                .split(',')
                .map(option => option.trim())
                .filter(Boolean)

          return (
            <div key={field.id}>
              <label className="hv-form-label">{field.label}</label>
              <select
                className="hv-select"
                value={value}
                onChange={e =>
                  setRightPrefill({
                    ...rightPrefill,
                    [field.field_key]: e.target.value
                  })
                }
              >
                <option value="">Leave blank</option>
                {options.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )
        }

        if (field.field_type === 'checkbox') {
          return (
            <label
              key={field.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={!!rightPrefill[field.field_key]}
                onChange={e =>
                  setRightPrefill({
                    ...rightPrefill,
                    [field.field_key]: e.target.checked
                  })
                }
              />
              {field.label}
            </label>
          )
        }

        return (
          <div key={field.id}>
            <label className="hv-form-label">{field.label}</label>
            <input
              className="hv-input"
              type={field.field_type === 'date' ? 'date' : 'text'}
              placeholder={`Pre-fill ${field.label}`}
              value={value}
              onChange={e =>
                setRightPrefill({
                  ...rightPrefill,
                  [field.field_key]: e.target.value
                })
              }
            />
          </div>
        )
      })}
    </div>
  </div>
)}

              <div>
                <label className="hv-form-label">Notes</label>
                <input
                  className="hv-input"
                  placeholder="Optional notes..."
                  value={rightForm.notes}
                  onChange={e => setRightForm({ ...rightForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button className="hv-btn hv-btn-secondary" onClick={() => setShowRightForm(false)}>
                Cancel
              </button>

              <button className="hv-btn hv-btn-primary" onClick={addRight} disabled={savingRight}>
                {savingRight ? 'Saving...' : 'Add Right'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function NotesBoard({
  notes,
  noteDraft,
  setNoteDraft,
  replyDrafts,
  setReplyDrafts,
  activeReplyId,
  setActiveReplyId,
  savingNote,
  onAddNote
}) {
  const topLevelNotes = notes.filter(note => !note.parent_note_id).slice().reverse()
  const repliesByParent = notes.reduce((acc, note) => {
    if (!note.parent_note_id) return acc
    if (!acc[note.parent_note_id]) acc[note.parent_note_id] = []
    acc[note.parent_note_id].push(note)
    return acc
  }, {})

  function getNoteAuthor(note) {
    return note.profile?.full_name || note.profile?.email || 'Unknown user'
  }

  return (
    <div className="concept-card concept-card-pad notes-board">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div className="section-title" style={{ marginBottom: 4 }}>
            Notes & Updates
          </div>

          <div style={{ fontSize: 13, color: 'var(--hv-text-muted)' }}>
            Post deal updates, internal notes and replies for your team.
          </div>
        </div>
      </div>

      <div className="note-composer">
        <label className="hv-form-label">Add update</label>
        <textarea
          className="hv-input"
          value={noteDraft}
          onChange={e => setNoteDraft(e.target.value)}
          placeholder="Write a note or update for this deal..."
          style={{ minHeight: 95, resize: 'vertical', lineHeight: 1.6, padding: 14 }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button
            className="hv-btn hv-btn-primary"
            disabled={savingNote || !noteDraft.trim()}
            onClick={() => onAddNote(null)}
          >
            {savingNote ? 'Posting...' : 'Add Update'}
          </button>
        </div>
      </div>

      {topLevelNotes.length === 0 ? (
        <div className="hv-empty" style={{ border: '1px dashed var(--hv-border)', borderRadius: 12 }}>
          <div className="hv-empty-title">No notes yet</div>
          <div className="hv-empty-text">Add the first update for this deal.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {topLevelNotes.map(note => {
            const replies = repliesByParent[note.id] || []
            const replyDraft = replyDrafts[note.id] || ''
            const isReplying = activeReplyId === note.id

            return (
              <div key={note.id} className="note-card">
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="note-avatar">{getInitials(getNoteAuthor(note))}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="note-meta">
                      <span className="note-author">{getNoteAuthor(note)}</span>
                      <span>•</span>
                      <span>{formatDateTime(note.created_at)}</span>
                    </div>

                    <div className="note-body">{note.body}</div>

                    <button
                      className="hv-btn hv-btn-ghost"
                      style={{ marginTop: 10, padding: 0, color: '#4F46E5', fontSize: 12 }}
                      onClick={() => setActiveReplyId(isReplying ? null : note.id)}
                    >
                      Reply{replies.length > 0 ? ` · ${replies.length}` : ''}
                    </button>

                    {replies.length > 0 && (
                      <div style={{ display: 'grid', gap: 10, marginTop: 12, paddingLeft: 16, borderLeft: '2px solid var(--hv-border-light)' }}>
                        {replies.map(reply => (
                          <div key={reply.id} style={{ display: 'flex', gap: 10 }}>
                            <div className="note-avatar" style={{ width: 28, height: 28, fontSize: 10 }}>
                              {getInitials(getNoteAuthor(reply))}
                            </div>

                            <div style={{ flex: 1 }}>
                              <div className="note-meta" style={{ marginBottom: 5 }}>
                                <span className="note-author">{getNoteAuthor(reply)}</span>
                                <span>•</span>
                                <span>{formatDateTime(reply.created_at)}</span>
                              </div>

                              <div className="note-body">{reply.body}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {isReplying && (
                      <div style={{ marginTop: 12, paddingLeft: 16, borderLeft: '2px solid #C7D2FE' }}>
                        <textarea
                          className="hv-input"
                          value={replyDraft}
                          onChange={e => setReplyDrafts(prev => ({ ...prev, [note.id]: e.target.value }))}
                          placeholder="Write a reply..."
                          style={{ minHeight: 72, resize: 'vertical', lineHeight: 1.6, padding: 12 }}
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                          <button
                            className="hv-btn hv-btn-secondary"
                            onClick={() => setActiveReplyId(null)}
                          >
                            Cancel
                          </button>

                          <button
                            className="hv-btn hv-btn-primary"
                            disabled={savingNote || !replyDraft.trim()}
                            onClick={() => onAddNote(note.id)}
                          >
                            {savingNote ? 'Posting...' : 'Post Reply'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DealOverviewCard({ deal, assignedProfile, formatDate }) {
  return (
    <div className="concept-card concept-card-pad">
      <div className="section-title">Deal Overview</div>

      <div className="detail-grid">
        <div className="detail-label">Sponsor</div>
        <div className="detail-value">{deal.clients?.name || '—'}</div>

        <div className="detail-label">Deal Type</div>
        <div className="detail-value">{deal.deal_type || '—'}</div>

        <div className="detail-label">Start Date</div>
        <div className="detail-value">{formatDate(deal.start_date)}</div>

        <div className="detail-label">End Date</div>
        <div className="detail-value">{formatDate(deal.end_date)}</div>

      <div className="detail-label">Payment Terms</div>
      <div className="detail-value">{deal.payment_terms || '—'}</div>

        <div className="detail-label">Account Manager</div>
        <div className="detail-value">
          {assignedProfile?.full_name || assignedProfile?.email || deal.owner || '—'}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--hv-border-light)', marginTop: 18, paddingTop: 18 }}>
        <div className="detail-label" style={{ marginBottom: 7 }}>Notes</div>
        <div style={{ fontSize: 12.5, color: 'var(--hv-text-secondary)', lineHeight: 1.6 }}>
          {deal.notes || 'No notes yet.'}
        </div>
      </div>
    </div>
  )
}

function BudgetCard({ sym, dealValue, allocatedTotal, allocationRows, standalone = false }) {
  const colors = ['#4F46E5', '#8B5CF6', '#FCA5A5', '#16A34A', '#F59E0B']

  return (
    <div className={standalone ? '' : 'concept-card concept-card-pad'}>
      <div className="section-title">Budget Allocation</div>

      <div style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
          {sym}{allocatedTotal.toLocaleString()}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--hv-text-muted)', marginLeft: 8 }}>
          Allocated {dealValue > 0 ? `(${Math.round((allocatedTotal / dealValue) * 100)}% of total value)` : ''}
        </span>
      </div>

      <div className="budget-bar">
        {allocationRows.map((allocation, index) => {
          const pct = dealValue > 0 ? Math.max((Number(allocation.amount || 0) / dealValue) * 100, 2) : 0
          return (
            <div
              key={allocation.id}
              className="budget-segment"
              style={{
                width: `${pct}%`,
                background: colors[index % colors.length]
              }}
            />
          )
        })}
      </div>

      {allocationRows.length === 0 ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: 13 }}>No project allocations yet.</div>
      ) : (
        allocationRows.map((allocation, index) => {
          const pct = dealValue > 0 ? Math.round((Number(allocation.amount || 0) / dealValue) * 100) : 0

          return (
            <div key={allocation.id} className="budget-row">
              <div style={{ fontWeight: 800 }}>
                <span className="budget-dot" style={{ background: colors[index % colors.length] }} />
                {allocation.projects?.name || 'Project'}
              </div>

              <div style={{ fontWeight: 800 }}>
                {sym}{Number(allocation.amount || 0).toLocaleString()}
              </div>

              <div style={{ color: 'var(--hv-text-muted)' }}>
                {pct}%
              </div>
            </div>
          )
        })
      )}

    </div>
  )
}

function RightsSummaryCard({
  rightsByProject,
  templateFields,
  expanded = false,
  expandedProjects,
  setExpandedProjects,
  expandedRights,
  setExpandedRights,
  onAddRight,
  onBulkCreateRights,
  onImportCsvRights,
  updateDeliveryLocal,
  saveDeliveryField,
  toggleDeliveryDone,
  fetchDeal,
  allocations,
  onCopyRights,
  canManageRights
}) {
  const toast = useToast()
  const [confirmDelete, setConfirmDelete] = useState(null)

  function toggleProject(projectId) {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }))
  }

  function toggleRight(rightId) {
    setExpandedRights(prev => ({
      ...prev,
      [rightId]: !prev[rightId]
    }))
  }

  function getTemplateKey(projectId, templateName) {
    return `template:${projectId}:${templateName}`
  }

  const groupedRights = rightsByProject.map(group => {
    const templateGroups = {}

    group.rights.forEach(right => {
      const templateName =
        right.right_templates?.name ||
        right.right_type ||
        'Other'

      if (!templateGroups[templateName]) {
        templateGroups[templateName] = []
      }

      templateGroups[templateName].push(right)
    })

    return {
      ...group,
      templateGroups
    }
  })

  const allRightsExpanded = groupedRights.every(group => {
    const templateEntries = Object.entries(group.templateGroups)

    if (templateEntries.length === 0) return true

    return templateEntries.every(([templateName, templateRights]) => {
      const templateKey = getTemplateKey(group.allocation.id, templateName)

      return (
        expandedRights[templateKey] &&
        templateRights.every(right => expandedRights[right.id])
      )
    })
  })

  function toggleAllRights() {
    if (allRightsExpanded) {
      const nextState = {}

      Object.keys(expandedRights).forEach(key => {
        if (!String(key).startsWith('template:')) {
          nextState[key] = expandedRights[key]
        }
      })

      setExpandedRights(nextState)
      return
    }

    const rightState = {}

    groupedRights.forEach(group => {
      Object.entries(group.templateGroups).forEach(
        ([templateName, templateRights]) => {
          rightState[getTemplateKey(group.allocation.id, templateName)] = true

          templateRights.forEach(right => {
            rightState[right.id] = true
          })
        }
      )
    })

    setExpandedRights(rightState)
  }

  async function executeDeleteRight(rightId) {
    const { error: deliveryError } = await supabase
      .from('deliveries')
      .delete()
      .eq('right_id', rightId)

    if (deliveryError) {
      console.error(deliveryError)
      toast.error(`Failed to delete delivery items: ${deliveryError.message}`)
      return
    }

    const { error } = await supabase
      .from('rights')
      .delete()
      .eq('id', rightId)

    if (error) {
      console.error(error)
      toast.error(`Failed to delete right: ${error.message}`)
      return
    }

    setExpandedRights(prev => {
      const next = { ...prev }
      delete next[rightId]
      return next
    })

    fetchDeal()
  }

  function requestDeleteRight(right) {
    setConfirmDelete({
      title: 'Delete right',
      message: `"${right.name}" and all of its delivery items will be permanently deleted.`,
      confirmLabel: 'Delete Right',
      onConfirm: async () => {
        setConfirmDelete(null)
        await executeDeleteRight(right.id)
      }
    })
  }

  async function executeDeleteCategory(templateName, templateRights) {
    const rightIds = templateRights.map(right => right.id)

    if (rightIds.length === 0) return

    const { error: deliveryError } = await supabase
      .from('deliveries')
      .delete()
      .in('right_id', rightIds)

    if (deliveryError) {
      console.error(deliveryError)
      toast.error(`Failed to delete delivery items: ${deliveryError.message}`)
      return
    }

    const { error: rightsError } = await supabase
      .from('rights')
      .delete()
      .in('id', rightIds)

    if (rightsError) {
      console.error(rightsError)
      toast.error(`Failed to delete category: ${rightsError.message}`)
      return
    }

    setExpandedRights(prev => {
      const next = { ...prev }

      Object.keys(next).forEach(key => {
        if (key.includes(`:${templateName}`) || rightIds.includes(key)) {
          delete next[key]
        }
      })

      rightIds.forEach(rightId => {
        delete next[rightId]
      })

      return next
    })

    fetchDeal()
  }

  function requestDeleteCategory(templateName, templateRights) {
    const deliveryCount = templateRights.reduce(
      (sum, right) => sum + (right.deliveries?.length || 0),
      0
    )

    setConfirmDelete({
      title: 'Delete category',
      message: `"${templateName}" will be removed from this project allocation, including ${templateRights.length} rights and ${deliveryCount} delivery items.`,
      confirmLabel: 'Delete Category',
      onConfirm: async () => {
        setConfirmDelete(null)
        await executeDeleteCategory(templateName, templateRights)
      }
    })
  }

  async function executeDeleteDeliveries(deliveriesToDelete) {
    const deliveryIds = deliveriesToDelete.map(delivery => delivery.id)
    const rightId = deliveriesToDelete[0]?.right_id

    if (deliveryIds.length === 0 || !rightId) return

    const allRightDeliveries = groupedRights
      .flatMap(group => group.rights)
      .find(right => right.id === rightId)?.deliveries || []
    const remainingCount = allRightDeliveries.filter(
      delivery => !deliveryIds.includes(delivery.id)
    ).length

    const { error: deliveryError } = await supabase
      .from('deliveries')
      .delete()
      .in('id', deliveryIds)

    if (deliveryError) {
      console.error(deliveryError)
      toast.error(`Failed to delete delivery items: ${deliveryError.message}`)
      return
    }

    if (remainingCount === 0) {
      const { error: rightError } = await supabase
        .from('rights')
        .delete()
        .eq('id', rightId)

      if (rightError) {
        console.error(rightError)
        toast.error(`Delivery items deleted, but right cleanup failed: ${rightError.message}`)
        fetchDeal()
        return
      }

      setExpandedRights(prev => {
        const next = { ...prev }
        delete next[rightId]
        return next
      })
    } else {
      const { error: quantityError } = await supabase
        .from('rights')
        .update({ quantity: remainingCount })
        .eq('id', rightId)

      if (quantityError) {
        console.error(quantityError)
        toast.error(`Delivery items deleted, but quantity update failed: ${quantityError.message}`)
      }
    }

    fetchDeal()
  }

  function requestDeleteDeliveries(deliveriesToDelete) {
    setConfirmDelete({
      title: 'Delete delivery items',
      message: `${deliveriesToDelete.length} selected delivery item${deliveriesToDelete.length === 1 ? '' : 's'} will be permanently deleted.${deliveriesToDelete.length === 1 ? '' : ''}`,
      confirmLabel: `Delete ${deliveriesToDelete.length} Item${deliveriesToDelete.length === 1 ? '' : 's'}`,
      onConfirm: async () => {
        setConfirmDelete(null)
        await executeDeleteDeliveries(deliveriesToDelete)
      }
    })
  }

  return (
    <div className={expanded ? '' : 'concept-card concept-card-pad'}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18
        }}
      >
        <div
          className="section-title"
          style={{ marginBottom: 0 }}
        >
          Rights & Deliveries
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canManageRights && <button
            className="hv-btn hv-btn-secondary"
            style={{
              fontSize: 11,
              padding: '7px 10px'
            }}
            onClick={toggleAllRights}
          >
            {allRightsExpanded ? 'Collapse rights' : 'Expand rights'}
          </button>}

          <button
            className="hv-btn hv-btn-primary"
            style={{
              fontSize: 11,
              padding: '7px 10px'
            }}
            onClick={onAddRight}
          >
            Add Right
          </button>

          {canManageRights && onImportCsvRights && (
            <button
              className="hv-btn hv-btn-secondary"
              style={{
                fontSize: 11,
                padding: '7px 10px'
              }}
              onClick={onImportCsvRights}
            >
              Import CSV
            </button>
          )}

          {canManageRights && <button
            className="hv-btn hv-btn-secondary"
            style={{
              fontSize: 11,
              padding: '7px 10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: '#4F46E5',
              borderColor: '#C7D2FE',
              background: '#EEF2FF'
            }}
            onClick={onBulkCreateRights}
          >
            <ListPlus size={14} />
            Bulk Create Rights
          </button>}
        </div>
      </div>

      {groupedRights.length === 0 ? (
        <div
          style={{
            color: 'var(--hv-text-muted)',
            fontSize: 13
          }}
        >
          No rights added yet.
        </div>
      ) : (
        groupedRights.map(group => {
          const isExpanded =
            expandedProjects[group.allocation.id]

          return (
            <div
              key={group.allocation.id}
              className="rights-project-row"
            >
              <div
                className="rights-project-header"
                onClick={() =>
                  toggleProject(group.allocation.id)
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <ChevronRight
                    size={14}
                    style={{
                      transform: isExpanded
                        ? 'rotate(90deg)'
                        : 'rotate(0deg)',
                      transition: '0.2s'
                    }}
                  />

                  {group.allocation.projects?.name ||
                    'Project'}
                </div>

                <div className="mini-progress">
                  <div
                    className="mini-progress-fill"
                    style={{
                      width: `${group.projectPct}%`,
                      background:
                        group.projectPct >= 80
                          ? '#16A34A'
                          : '#F59E0B'
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 10,
                    color: 'var(--hv-text-secondary)'
                  }}
                >
                  <span>
                    {group.projectComplete} /{' '}
                    {group.projectTotal || 0} delivered
                  </span>

                  {onCopyRights && group.rights.length > 0 && allocations.length > 1 && (
                    <button
                      className="hv-btn hv-btn-secondary"
                      style={{
                        fontSize: 11,
                        padding: '5px 8px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={e => {
                        e.stopPropagation()
                        onCopyRights(group)
                      }}
                    >
                      Copy rights
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div style={{ marginTop: 12 }}>
                  {group.rights.length === 0 ? (
                    <div
                      style={{
                        color: 'var(--hv-text-muted)',
                        fontSize: 12
                      }}
                    >
                      No rights for this allocation.
                    </div>
                  ) : (
                    Object.entries(group.templateGroups).map(
                      ([templateName, templateRights]) => {
                        const templateKey = getTemplateKey(
                          group.allocation.id,
                          templateName
                        )

                        const templateExpanded =
                          expandedRights[templateKey]

                        const templateComplete = templateRights.reduce(
                          (sum, right) => sum + right.completed,
                          0
                        )

                        const templateTotal = templateRights.reduce(
                          (sum, right) => sum + right.total,
                          0
                        )

                        const templatePct = templateTotal > 0
                          ? Math.round((templateComplete / templateTotal) * 100)
                          : 0

                        return (
                          <div key={templateKey}>
                            <div
                              className="right-line"
                              onClick={() =>
                                setExpandedRights(prev => ({
                                  ...prev,
                                  [templateKey]: !prev[templateKey]
                                }))
                              }
                              style={{
                                background: '#F8FAFC',
                                fontWeight: 800
                              }}
                            >
                              <ChevronRight
                                size={13}
                                style={{
                                  transform: templateExpanded
                                    ? 'rotate(90deg)'
                                    : 'rotate(0deg)',
                                  transition: '0.2s'
                                }}
                              />

                              <div>{templateName}</div>

                              <div
                                style={{
                                  color: 'var(--hv-text-muted)'
                                }}
                              >
                                {templateComplete} / {templateTotal} delivered
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10
                                }}
                              >
                                <div
                                  className="mini-progress"
                                  style={{ width: 90 }}
                                >
                                  <div
                                    className="mini-progress-fill"
                                    style={{
                                      width: `${templatePct}%`,
                                      background:
                                        templatePct >= 80
                                          ? '#16A34A'
                                          : '#F59E0B'
                                    }}
                                  />
                                </div>

                                {canManageRights && <button
                                  className="hv-btn hv-btn-ghost"
                                  style={{
                                    padding: '4px 6px',
                                    color: '#EF4444',
                                    fontSize: 11
                                  }}
                                  onClick={e => {
                                    e.stopPropagation()
                                    requestDeleteCategory(templateName, templateRights)
                                  }}
                                >
                                  Delete category
                                </button>}
                              </div>
                            </div>

                            {templateExpanded && (
                              <div style={{ marginLeft: 18 }}>
                                {templateRights.map(right => {
                                  const rightExpanded =
                                    expandedRights[right.id]

                                  return (
                                    <div key={right.id}>
                                      <div
                                        className="right-line"
                                        onClick={() =>
                                          toggleRight(right.id)
                                        }
                                      >
                                        <ChevronRight
                                          size={13}
                                          style={{
                                            transform: rightExpanded
                                              ? 'rotate(90deg)'
                                              : 'rotate(0deg)',
                                            transition: '0.2s'
                                          }}
                                        />

                                        <div style={{ fontWeight: 700 }}>
                                          {right.name}
                                        </div>

                                        <div
                                          style={{
                                            color:
                                              'var(--hv-text-muted)'
                                          }}
                                        >
                                          {right.completed} /{' '}
                                          {right.total || 0} delivered
                                        </div>

                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                          }}
                                        >
                                          {right.pct >= 100 ? (
                                            <CircleCheck
                                              size={15}
                                              color="#16A34A"
                                            />
                                          ) : (
                                            <CircleAlert
                                              size={15}
                                              color="#F59E0B"
                                            />
                                          )}

                                          {canManageRights && <button
                                            className="hv-btn hv-btn-ghost"
                                            style={{
                                              padding: 4,
                                              color: '#EF4444'
                                            }}
                                            onClick={e => {
                                              e.stopPropagation()
                                              requestDeleteRight(right)
                                            }}
                                          >
                                            Delete
                                          </button>}
                                        </div>
                                      </div>

                                      {rightExpanded && (
                                        <DeliveryGrid
                                          deliveries={
                                            right.deliveries
                                          }
                                          fields={
                                            templateFields[
                                              right.template_id
                                            ] || []
                                          }
                                          updateDeliveryLocal={
                                            updateDeliveryLocal
                                          }
                                          saveDeliveryField={
                                            saveDeliveryField
                                          }
                                          toggleDeliveryDone={
                                            toggleDeliveryDone
                                          }
                                          onDeleteDeliveries={
                                            requestDeleteDeliveries
                                          }
                                          canManageRights={canManageRights}
                                        />
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }
                    )
                  )}
                </div>
              )}
            </div>
          )
        })
      )}

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.title}
          message={confirmDelete.message}
          confirmLabel={confirmDelete.confirmLabel}
          onConfirm={confirmDelete.onConfirm}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}

function DeliveryGrid({
  deliveries,
  fields,
  updateDeliveryLocal,
  saveDeliveryField,
  toggleDeliveryDone,
  onDeleteDeliveries,
  canManageRights
}) {
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState([])

  useEffect(() => {
    const visibleIds = deliveries.map(delivery => delivery.id)

    setSelectedDeliveryIds(prev =>
      prev.filter(deliveryId => visibleIds.includes(deliveryId))
    )
  }, [deliveries])

  if (!deliveries.length) {
    return (
      <div style={{ color: 'var(--hv-text-muted)', fontSize: 12, marginLeft: 26, marginBottom: 12 }}>
        No delivery items yet.
      </div>
    )
  }

  async function saveCustomField(delivery, fieldKey, value) {
    const updatedFields = {
      ...(delivery.custom_fields || {}),
      [fieldKey]: value
    }

    const { error } = await supabase
      .from('deliveries')
      .update({
        custom_fields: updatedFields
      })
      .eq('id', delivery.id)

    if (error) {
      console.error('Failed to update custom field:', error)
    }
  }

  const allSelected = selectedDeliveryIds.length === deliveries.length
  const selectedDeliveries = deliveries.filter(delivery =>
    selectedDeliveryIds.includes(delivery.id)
  )
  const gridTemplateColumns = `44px 1.3fr ${fields.map(() => '1fr').join(' ')} 80px`

  function toggleDeliverySelection(deliveryId) {
    setSelectedDeliveryIds(prev =>
      prev.includes(deliveryId)
        ? prev.filter(id => id !== deliveryId)
        : [...prev, deliveryId]
    )
  }

  function toggleAllDeliveries() {
    setSelectedDeliveryIds(allSelected ? [] : deliveries.map(delivery => delivery.id))
  }

  return (
    <div className="delivery-grid" style={{ pointerEvents: canManageRights ? 'auto' : 'none' }}>
      {selectedDeliveryIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderBottom: '1px solid var(--hv-border-light)',
            background: '#FFF7ED'
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
            {selectedDeliveryIds.length} selected
          </div>

          <button
            className="hv-btn hv-btn-secondary"
            style={{
              fontSize: 11,
              padding: '6px 9px',
              color: '#EF4444',
              borderColor: '#FCA5A5'
            }}
            onClick={() => onDeleteDeliveries(selectedDeliveries)}
          >
            Delete selected
          </button>
        </div>
      )}

      <div
        className="delivery-grid-head"
        style={{
          gridTemplateColumns
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAllDeliveries}
            aria-label="Select all delivery items"
          />
        </div>

        <div>Item</div>

        {fields.map(field => (
          <div key={field.id}>{field.label}</div>
        ))}

        <div style={{ textAlign: 'center' }}>Done</div>
      </div>

      {deliveries.map(delivery => {
        const done =
          delivery.status === 'delivered' ||
          delivery.status === 'approved'

        return (
          <div
            key={delivery.id}
            className="delivery-grid-row"
            style={{
              gridTemplateColumns
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <input
                type="checkbox"
                checked={selectedDeliveryIds.includes(delivery.id)}
                onChange={() => toggleDeliverySelection(delivery.id)}
                aria-label={`Select ${delivery.name || 'delivery item'}`}
              />
            </div>

            <input
              className="hv-input"
              value={delivery.name || ''}
              placeholder="Delivery item"
              onChange={e =>
                updateDeliveryLocal(delivery.id, 'name', e.target.value)
              }
              onBlur={e =>
                saveDeliveryField(delivery.id, 'name', e.target.value)
              }
            />

            {fields.map(field => {
              const value =
                delivery.custom_fields?.[field.field_key] ?? ''

              if (field.field_type === 'select') {
  const options = Array.isArray(field.options)
    ? field.options
    : String(field.options || '')
        .split(',')
        .map(option => option.trim())
        .filter(Boolean)

  return (
    <select
      key={field.id}
      className="hv-select"
      value={value}
onChange={e => {
  const updated = {
    ...(delivery.custom_fields || {}),
    [field.field_key]: e.target.value
  }

  updateDeliveryLocal(
    delivery.id,
    'custom_fields',
    updated
  )

  saveCustomField(
    delivery,
    field.field_key,
    e.target.value
  )
}}
    >
      <option value="">Select...</option>

      {options.map(option => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

              if (field.field_type === 'checkbox') {
                return (
                  <div
                    key={field.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={e =>
                        saveCustomField(
                          delivery,
                          field.field_key,
                          e.target.checked
                        )
                      }
                    />
                  </div>
                )
              }

              return (
                <input
                  key={field.id}
                  className="hv-input"
                  type={field.field_type === 'date' ? 'date' : 'text'}
                  value={value}
                  placeholder={field.label}
                  onChange={e => {
                    const updated = {
                      ...(delivery.custom_fields || {}),
                      [field.field_key]: e.target.value
                    }

                    updateDeliveryLocal(
                      delivery.id,
                      'custom_fields',
                      updated
                    )
                  }}
                  onBlur={e =>
                    saveCustomField(
                      delivery,
                      field.field_key,
                      e.target.value
                    )
                  }
                />
              )
            })}

            <div className="done-cell">
              <input
                type="checkbox"
                checked={done}
                onChange={() => toggleDeliveryDone(delivery)}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ActivityPanel({ activity, embedded = false }) {
  const groupedActivity = groupActivityByDay(activity)
  const groupLabels = Object.keys(groupedActivity)

  return (
    <div className={embedded ? '' : 'activity-panel'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 22 }}>
        <div>
          <div className="activity-panel-title" style={{ marginBottom: 5 }}>Activity</div>
          <div style={{ fontSize: 13, color: 'var(--hv-text-muted)' }}>
            Audit-style timeline of deal changes, rights, deliveries, budget allocations and notes.
          </div>
        </div>

        {activity.length > 0 && (
          <span className="hv-badge hv-badge-gray">
            {activity.length} {activity.length === 1 ? 'event' : 'events'}
          </span>
        )}
      </div>

      {activity.length === 0 ? (
        <div className="hv-empty" style={{ border: '1px dashed var(--hv-border)', borderRadius: 12 }}>
          <div className="hv-empty-title">No activity yet</div>
          <div className="hv-empty-text">Deal activity will appear here as your team works on it.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 22 }}>
          {groupLabels.map(label => (
            <div key={label}>
              <div className="activity-group-label">{label}</div>

              <div style={{ display: 'grid', gap: 0 }}>
                {groupedActivity[label].map(item => (
                  <div key={item.id} className="activity-item">
                    <div className="activity-dot">{getInitials(item.actor)}</div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
                          {item.title}
                        </span>
                        <span className="hv-badge hv-badge-gray" style={{ fontSize: 10 }}>
                          {item.type}
                        </span>
                      </div>

                      <div style={{ fontSize: 12.5, color: 'var(--hv-text-secondary)', lineHeight: 1.5 }}>
                        {item.description}
                      </div>

                      <div style={{ fontSize: 11.5, color: 'var(--hv-text-muted)', marginTop: 7 }}>
                        By {item.actor}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', minWidth: 120 }}>
                      <div style={{ fontSize: 11.5, color: 'var(--hv-text-primary)', fontWeight: 700 }}>
                        {formatActivityTimestamp(item.timestamp)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--hv-text-muted)', marginTop: 4 }}>
                        {getRelativeTime(item.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
